// Authentication service for KATA Dashboard

import { apiClient, handleApiError, setCachedUser, getCachedUser } from './api';
import { LoginCredentials, LoginResponse, User, UserProfileUpdate } from '../types/auth';

export class AuthService {
  /**
   * Login user with credentials
   */
  static async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login/', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Logout user
   */
  static async logout(refreshToken: string): Promise<void> {
    try {
      await apiClient.post('/auth/logout/', { refresh: refreshToken });
    } catch (error: any) {
      // Even if logout fails on server, we should clear local tokens
      console.warn('Logout request failed:', handleApiError(error));
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(refreshToken: string): Promise<{ access: string }> {
    try {
      const response = await apiClient.post<{ access: string }>('/auth/token/refresh/', {
        refresh: refreshToken,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Verify token validity
   */
  static async verifyToken(token: string): Promise<boolean> {
    try {
      await apiClient.post('/auth/token/verify/', { token });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get current user profile (with caching)
   */
  static async getCurrentUser(): Promise<User> {
    // Try to get cached user first
    const cachedUser = getCachedUser();
    if (cachedUser) {
      return cachedUser;
    }

    // If no cache, fetch from API
    try {
      const response = await apiClient.get<User>('/users/me/');
      const user = response.data;
      
      // Cache the user data
      setCachedUser(user);
      
      return user;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get current user profile from cache only (no API call)
   */
  static getCachedCurrentUser(): User | null {
    return getCachedUser();
  }

  /**
   * Force refresh user data from API
   */
  static async refreshCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/users/me/');
      const user = response.data;
      
      // Update cache
      setCachedUser(user);
      
      return user;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get user profile (alternative endpoint)
   */
  static async getUserProfile(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/users/profile/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update user profile
   */
  static async updateProfile(updates: UserProfileUpdate): Promise<User> {
    try {
      const response = await apiClient.patch<User>('/users/profile/', updates);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Check if user has admin privileges
   */
  static isAdmin(user: User | null): boolean {
    return user?.is_admin || false;
  }

  /**
   * Get user's display name
   */
  static getUserDisplayName(user: User | null): string {
    if (!user) return 'Unknown User';
    return user.full_name || user.username || 'Unknown User';
  }

  /**
   * Get user's role display
   */
  static getUserRole(user: User | null): string {
    return user?.role_display || 'User';
  }
}
